import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Tabs,
  Tab,
  Avatar,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  AdminPanelSettings,
  School as InstructorIcon,
  Person,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/ClerkAuthContext';
import ClerkUserService, { ClerkUser, RoleType } from '../services/clerk-user-service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`roles-tabpanel-${index}`}
      aria-labelledby={`roles-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `roles-tab-${index}`,
    'aria-controls': `roles-tabpanel-${index}`,
  };
}

export default function RolesPage() {
  const [tabValue, setTabValue] = useState(0);
  const [admins, setAdmins] = useState<ClerkUser[]>([]);
  const [instructors, setInstructors] = useState<ClerkUser[]>([]);
  const [allUsers, setAllUsers] = useState<ClerkUser[]>([]); // For user search/promotion
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog state for adding/promoting users
  const [dialogOpen, setDialogOpen] = useState(false);
  const [promotingToRole, setPromotingToRole] = useState<RoleType>('admin');
  const [selectedUser, setSelectedUser] = useState<ClerkUser | null>(null);
  const [searchText, setSearchText] = useState('');
  const [promotableUsers, setPromotableUsers] = useState<ClerkUser[]>([]);
  
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadClerkUsers();
  }, []);

  const loadClerkUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allClerkUsers = await ClerkUserService.getAllUsers();
      const adminUsers = await ClerkUserService.getAdminUsers();
      const instructorUsers = await ClerkUserService.getInstructorUsers();

      setAllUsers(allClerkUsers);
      setAdmins(adminUsers);
      setInstructors(instructorUsers);
      
    } catch (err) {
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenPromotionDialog = async (role: RoleType) => {
    setPromotingToRole(role);
    setSelectedUser(null);
    setSearchText('');
    
    // Load promotable users for this role
    try {
      const users = await ClerkUserService.getPromotableUsers(role);
      setPromotableUsers(users);
    } catch (error) {
      // Fallback to filtering all users
      if (role === 'instructor') {
        setPromotableUsers(allUsers.filter(u => u.role !== 'instructor'));
      } else {
        setPromotableUsers(allUsers.filter(u => u.role !== 'admin' && u.role !== 'super_admin'));
      }
    }
    
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setSearchText('');
  };

  const handlePromoteUser = async () => {
    if (!selectedUser) return;

    try {
      await ClerkUserService.updateUserRole(selectedUser.id, promotingToRole);
      
      // Show success message - TODO: Replace with proper toast notification
      // eslint-disable-next-line no-alert
      alert(`Successfully promoted ${selectedUser.email} to ${promotingToRole} role!`);
      
      handleCloseDialog();
      // Reload users after promotion
      await loadClerkUsers();
      
    } catch (err) {
      setError('Failed to promote user. Please try again.');
    }
  };

  const handleRoleChange = async (user: ClerkUser, newRole: RoleType | null) => {
    try {
      await ClerkUserService.updateUserRole(user.id, newRole);
      
      // TODO: Replace with proper toast notification
      // eslint-disable-next-line no-alert
      alert(`Successfully changed ${user.email} role to ${newRole || 'none'}!`);
      
      // Reload users after role change
      await loadClerkUsers();
      
    } catch (err) {
      setError('Failed to update user role. Please try again.');
    }
  };

  const getRoleIcon = (role: string | null) => {
    switch (role) {
      case 'super_admin':
        return <AdminPanelSettings color="primary" />;
      case 'admin':
        return <AdminPanelSettings />;
      case 'instructor':
        return <InstructorIcon color="secondary" />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: string | null): "primary" | "secondary" | "default" | "success" => {
    switch (role) {
      case 'super_admin':
        return 'primary';
      case 'admin':
        return 'secondary';
      case 'instructor':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleDisplayName = (role: string | null): string => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'instructor':
        return 'Instructor';
      default:
        return 'No Role';
    }
  };


  const renderUserTable = (users: ClerkUser[]) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Last Sign In</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar 
                    src={user.imageUrl || undefined}
                    sx={{ width: 32, height: 32 }}
                  >
                    {user.firstName?.[0] || user.email[0].toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'No Name'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {user.id}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{user.email}</Typography>
                {user.email === currentUser?.email && (
                  <Chip label="You" size="small" sx={{ ml: 1 }} />
                )}
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {getRoleIcon(user.role)}
                  <Chip 
                    label={getRoleDisplayName(user.role)} 
                    size="small"
                    color={getRoleColor(user.role)}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {new Date(user.createdAt).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {user.lastSignInAt 
                    ? new Date(user.lastSignInAt).toLocaleString()
                    : 'Never'
                  }
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Change Role">
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      // TODO: Replace with proper role selection dialog
                      // eslint-disable-next-line no-alert
                      const newRole = prompt(`Change role for ${user.email}. Current: ${user.role || 'none'}. Enter new role (admin/super_admin/instructor) or 'none' to remove:`);
                      if (newRole !== null) {
                        const validRole = newRole === 'none' ? null : newRole as RoleType;
                        handleRoleChange(user, validRole);
                      }
                    }}
                    disabled={user.email === currentUser?.email} // Can't change your own role
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading users...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Roles Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="roles tabs">
          <Tab 
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <AdminPanelSettings />
                Admins ({admins.length})
              </Box>
            } 
            {...a11yProps(0)} 
          />
          <Tab 
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <InstructorIcon />
                Instructors ({instructors.length})
              </Box>
            } 
            {...a11yProps(1)} 
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Admin Users
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPromotionDialog('admin')}
          >
            Promote to Admin
          </Button>
        </Box>
        {renderUserTable(admins)}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Instructor Users
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPromotionDialog('instructor')}
          >
            Promote to Instructor
          </Button>
        </Box>
        {renderUserTable(instructors)}
      </TabPanel>

      {/* User Promotion Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Promote User to {getRoleDisplayName(promotingToRole)}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={promotableUsers}
                getOptionLabel={(option) => `${option.email} - ${option.firstName || ''} ${option.lastName || ''}`.trim()}
                value={selectedUser}
                onChange={(event, newValue) => setSelectedUser(newValue)}
                inputValue={searchText}
                onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Users"
                    placeholder="Type to search by email or name..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <Avatar 
                        src={option.imageUrl || undefined}
                        sx={{ width: 32, height: 32 }}
                      >
                        {option.firstName?.[0] || option.email[0].toUpperCase()}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="body2">
                          {option.firstName && option.lastName ? `${option.firstName} ${option.lastName}` : 'No Name'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email} • Current role: {getRoleDisplayName(option.role)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                noOptionsText={`No users available for ${promotingToRole} promotion`}
              />
            </Grid>
            {selectedUser && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Selected:</strong> {selectedUser.email}<br />
                    <strong>Current Role:</strong> {getRoleDisplayName(selectedUser.role)}<br />
                    <strong>New Role:</strong> {getRoleDisplayName(promotingToRole)}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handlePromoteUser} 
            variant="contained"
            disabled={!selectedUser}
          >
            Promote User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}